import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useMutation } from "convex/react";
import { useUser } from "@clerk/clerk-react";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useAdminMode } from "../contexts/AdminModeContext";
import { api } from "convex/_generated/api";
import { Music, Users, Building2, Loader2 } from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AddressAutocomplete } from "../components/forms/AddressAutocomplete";
import { cn } from "../lib/utils";

type AccountType = "dancer" | "organizer" | "studio";

const ACCOUNT_TYPES = [
  {
    type: "dancer" as const,
    label: "Dancer",
    description: "Browse and discover social dance events in your area",
    icon: Music,
  },
  {
    type: "organizer" as const,
    label: "Organizer",
    description: "Post and manage events — socials, workshops, and more",
    icon: Users,
  },
  {
    type: "studio" as const,
    label: "Studio / Venue",
    description: "Register your studio or venue and host events on-site",
    icon: Building2,
  },
];

export function OnboardingPage() {
  const { user: clerkUser } = useUser();
  const { user: convexUser, isLoading } = useCurrentUser();
  const { isAdminMode } = useAdminMode();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (isAdminMode) return;
    if (!isLoading && convexUser?.accountType) navigate("/dashboard");
  }, [isLoading, convexUser, isAdminMode]);
  const [selectedType, setSelectedType] = useState<AccountType | null>(null);
  const [studioName, setStudioName] = useState("");
  const [studioAddress, setStudioAddress] = useState("");
  const [studioCoords, setStudioCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getOrCreateUser = useMutation(api.users.getOrCreateUser);
  const updateAccountType = useMutation(api.users.updateAccountType);

  const handleContinue = async () => {
    if (!selectedType) return;

    if (isAdminMode) {
      navigate("/dashboard");
      return;
    }

    setIsSubmitting(true);
    try {
      await getOrCreateUser();
      await updateAccountType({
        accountType: selectedType,
        studioName: selectedType === "studio" ? studioName || undefined : undefined,
        studioAddress: selectedType === "studio" ? studioAddress || undefined : undefined,
        studioLatitude: selectedType === "studio" ? studioCoords?.latitude : undefined,
        studioLongitude: selectedType === "studio" ? studioCoords?.longitude : undefined,
      });
      navigate("/dashboard");
    } catch (error) {
      console.error("Onboarding failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <div className="max-w-lg mx-auto py-12">
        <header className="mb-8 text-center">
          <h1 className="font-display text-3xl text-foreground mb-2">
            Welcome{clerkUser?.firstName ? `, ${clerkUser.firstName}` : ""}!
          </h1>
          <p className="text-muted-foreground">
            How will you use NE Dances?
          </p>
        </header>

        <div className="space-y-3 mb-8">
          {ACCOUNT_TYPES.map(({ type, label, description, icon: Icon }) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={cn(
                "w-full p-4 rounded-xl border text-left transition-all duration-200 flex items-center gap-4",
                selectedType === type
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-border/60 hover:bg-secondary/30"
              )}
            >
              <div
                className={cn(
                  "p-3 rounded-lg",
                  selectedType === type
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground"
                )}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-foreground">{label}</p>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Studio details */}
        {selectedType === "studio" && (
          <div className="space-y-4 mb-8 p-4 rounded-xl border border-border bg-card">
            <h3 className="text-sm font-semibold text-foreground">Studio Details</h3>
            <div className="space-y-2">
              <Label>Studio Name</Label>
              <Input
                value={studioName}
                onChange={(e) => setStudioName(e.target.value)}
                placeholder="e.g., Rhythm Dance Studio"
              />
            </div>
            <div className="space-y-2">
              <Label>Studio Address</Label>
              <AddressAutocomplete
                value={studioAddress}
                onChange={(address) => {
                  setStudioAddress(address);
                  setStudioCoords(null);
                }}
                onPlaceSelect={(place) => {
                  setStudioAddress(place.address);
                  setStudioCoords(place.coords);
                }}
                placeholder="Start typing your studio address..."
              />
            </div>
          </div>
        )}

        <Button
          size="lg"
          className="w-full"
          disabled={!selectedType || isSubmitting}
          onClick={handleContinue}
        >
          {isAdminMode ? (
            "Preview Continue →"
          ) : isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Setting up...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </div>
    </PageContainer>
  );
}
